/**
 * SEO Head Component for Multilingual Support
 * 
 * Provides comprehensive SEO features for internationalization:
 * - Hreflang tags for all supported languages
 * - Localized meta descriptions and titles
 * - Open Graph tags with language support
 * - Canonical URLs with proper locale handling
 * - JSON-LD structured data with language context
 * 
 * @param {string} title - Page title
 * @param {string} description - Page description
 * @param {string} path - Current page path (without domain)
 * @param {string} locale - Current locale
 * @param {Object} openGraph - Open Graph specific data
 * 
 * @description Comprehensive SEO component with multilingual support
 */

import Head from 'next/head';
import { useRouter } from 'next/router';

const SEOHead = ({ 
  title = 'WOODALPS - Handcrafted Wooden Bicycles',
  description = 'Discover WoodAlps\' handcrafted wooden bicycles, made in the French Alps. Experience the perfect blend of traditional craftsmanship and modern technology.',
  path = '',
  locale = 'en',
  openGraph = {}
}) => {
  const router = useRouter();
  const currentPath = router.asPath.split('?')[0]; // Remove query params
  
  // Base domain - update this to your actual domain
  const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://woodalps.com';
  
  // Supported locales
  const supportedLocales = ['en', 'fr', 'de', 'it'];
  
  // Generate canonical URL
  const canonicalUrl = `${baseUrl}${locale === 'en' ? '' : `/${locale}`}${currentPath}`;
  
  // Generate hreflang URLs
  const hreflangs = supportedLocales.map(lang => ({
    locale: lang,
    url: `${baseUrl}${lang === 'en' ? '' : `/${lang}`}${currentPath}`
  }));
  
  // Localized Open Graph data
  const ogData = {
    title: title,
    description: description,
    url: canonicalUrl,
    locale: locale,
    type: 'website',
    siteName: 'WoodAlps',
    ...openGraph
  };
  
  return (
    <Head>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang Tags */}
      {hreflangs.map(({ locale: hrefLang, url }) => (
        <link
          key={hrefLang}
          rel="alternate"
          hrefLang={hrefLang}
          href={url}
        />
      ))}
      
      {/* Default hreflang for unmatched languages */}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={hreflangs.find(h => h.locale === 'en')?.url || canonicalUrl}
      />
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={ogData.title} />
      <meta property="og:description" content={ogData.description} />
      <meta property="og:url" content={ogData.url} />
      <meta property="og:type" content={ogData.type} />
      <meta property="og:locale" content={locale} />
      <meta property="og:site_name" content={ogData.siteName} />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogData.title} />
      <meta name="twitter:description" content={ogData.description} />
      
      {/* Additional Open Graph image if provided */}
      {ogData.image && (
        <>
          <meta property="og:image" content={ogData.image} />
          <meta name="twitter:image" content={ogData.image} />
          {ogData.imageAlt && (
            <meta property="og:image:alt" content={ogData.imageAlt} />
          )}
        </>
      )}
      
      {/* Language and region tags */}
      <meta httpEquiv="content-language" content={locale} />
      
      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'WoodAlps',
            url: baseUrl,
            logo: `${baseUrl}/images/logoWA.png`,
            description: 'Handcrafted wooden bicycles made in the French Alps',
            address: {
              '@type': 'PostalAddress',
              addressCountry: 'FR'
            },
            inLanguage: supportedLocales,
            sameAs: [
              'https://www.instagram.com/woodalps_wooden_bikes/',
              'https://facebook.com/woodalps'
            ]
          })
        }}
      />
    </Head>
  );
};

export default SEOHead;
